import { useState } from 'react';
import { X } from 'lucide-react';
import { klassenbuchApi } from '../../lib/klassenbuchApi';
import type { ExcuseKind, KlassenbuchParticipant } from '../../lib/klassenbuchApi';
import { apiClient } from '../../lib/api';
import toast from 'react-hot-toast';

type Props = {
  participant: KlassenbuchParticipant | null;
  defaultFrom?: string;
  defaultTo?: string;
  onClose: () => void;
  onCreated?: () => void;
};

/**
 * Dialog zum Erfassen einer Entschuldigung oder Beurlaubung inkl. optionalem
 * Datei-Upload über die bestehende student_files-Pipeline.
 */
export default function ExcuseModal({ participant, defaultFrom, defaultTo, onClose, onCreated }: Props) {
  const [kind, setKind] = useState<ExcuseKind>('excuse');
  const [validFrom, setValidFrom] = useState(defaultFrom || new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState(defaultTo || new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  if (!participant) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!participant) return;
    setSaving(true);
    try {
      let documentFileId: number | null = null;
      if (file) {
        const unid = String(
          participant.sophomorixUnid || participant.Student_ID || participant.username || ''
        );
        if (unid) {
          const form = new FormData();
          form.append('file', file);
          form.append('file_type', 'private');
          form.append('category', 'excuse');
          try {
            const uploadRes = await apiClient.post(
              `/student-files/${encodeURIComponent(unid)}/upload`,
              form
            );
            documentFileId =
              uploadRes.data?.data?.file_id ??
              uploadRes.data?.data?.id ??
              uploadRes.data?.file_id ??
              null;
          } catch (uploadErr) {
            console.warn('[ExcuseModal] Datei-Upload fehlgeschlagen, speichere Entschuldigung ohne Datei', uploadErr);
            toast.error('Datei-Upload fehlgeschlagen, Entschuldigung wird ohne Attest gespeichert.');
          }
        }
      }

      await klassenbuchApi.createExcuse({
        student_id: participant.user_id,
        kind,
        valid_from: validFrom,
        valid_to: validTo,
        reason,
        document_file_id: documentFileId,
        source: 'teacher'
      });

      toast.success('Entschuldigung gespeichert');
      onCreated?.();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            Entschuldigung / Beurlaubung
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-900">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="text-sm text-gray-700">
            <span className="font-medium">
              {(participant.last_name || participant.Name || '') +
                (participant.first_name || participant.Vorname ? ', ' : '') +
                (participant.first_name || participant.Vorname || participant.username)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700">Art</span>
              <select
                className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                value={kind}
                onChange={(e) => setKind(e.target.value as ExcuseKind)}
              >
                <option value="excuse">Entschuldigung</option>
                <option value="beurlaubung">Beurlaubung</option>
                <option value="sick">Krankmeldung</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">Dokument (optional)</span>
              <input
                type="file"
                className="mt-1 w-full text-sm"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700">Gültig von</span>
              <input
                type="date"
                className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                required
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">Gültig bis</span>
              <input
                type="date"
                className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                required
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="text-gray-700">Begründung</span>
            <textarea
              className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm min-h-[80px]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. ärztliches Attest, Familienfeier, …"
            />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
